/* eslint-disable @typescript-eslint/no-unused-vars */
import { OrganizationInfo, OutlookRecipientDto, UidGenerator } from "@/index";
import { ResourceService } from "@/services/resource-service/ResourceService";
import { IFilterResource, ResourceInfo } from "@/services/resource-service/entity/ResourceEntity";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { IResourceStore } from "./ResourceReducerModel";
import { AttendeeInfo } from "@/services/attendee-service/entity/AttendeeEntity";
import { RcResourceService } from "@/services/rc-resource-service/RcResourceService";
import { OrderDataRequest } from "@/services/rc-resource-service/entity/OrderDataRequest";
import { ExternalBookingResource } from "@/services/rc-resource-service/entity/request";

export const FILTER_DEFAULT: IFilterResource = {
  name: "",
  locationIds: [],
  categoryIds: [],
  categoryCondition: "or",
  isAvailable: true,
};

const initialState: IResourceStore = {
  originalSelectedResources: [],
  selectedResource: [],
  resourceProcessingMoving: [],
  loading: false,
  filterResource: FILTER_DEFAULT,
  resourceSelectedInOutlook: null,
  attendees: {
    mainAttendee: null,
    listAttendee: [],
  },
  isLoadingSelectedRs: false,
};

function copyOrderFromOriginal(state: IResourceStore, resource: ResourceInfo): ResourceInfo {
  const originalResource = state.originalSelectedResources?.find((item) => item.guid === resource.guid);
  if (originalResource) {
    resource.orders = cloneDeep(originalResource.orders);
  }
  return resource;
}

export const fetchAndUpdateResources = createAsyncThunk("resource/fetchAndUpdateResources", async ({ resources, orgInfo }: { resources: ResourceInfo[]; orgInfo: OrganizationInfo }) => {
  const rcResourceService = new RcResourceService();
  // const iCalUId = orgInfo?.iCalUId || orgInfo?.iCalUIds?.[0]?.iCalUids[0];
  // const iCalUIdString = typeof iCalUId === "string" ? iCalUId : "";

  const payloadApi: ExternalBookingResource[] = resources?.map((resource) => ({
    ICalUID: orgInfo?.iCalUIds?.find((item) => item.adId === resource.adId)?.iCalUids[0] || "",
    ResourceAdId: resource.adId,
  }));

  if (payloadApi.length === 0) return resources;

  const payload: OrderDataRequest = {
    BaseUrl: orgInfo.resourceCentralEndpoint + "/ResourceCentral/api/OfficeApp/GetExtenalBookingResources",
    Payload: payloadApi,
  };

  const response = await rcResourceService.fetchExternalBookingResources(payload);

  if (!response.data) return resources;

  return resources.map((resource) => {
    const orderInfo = response.data.find((order) => order.ResourceAdId === resource.adId);
    if (orderInfo) {
      resource.orders = {
        iCalUID: orderInfo.ICalUID || "",
        resourceAdId: orderInfo.ResourceAdId,
        guid: orderInfo.Guid,
        hasOrder: orderInfo.HasOrder,
        haveNormalOrder: orderInfo.HaveNormalOrder,
      };
      return resource;
    }
    return resource;
  });
});

export const resourceSlice = createSlice({
  name: "resource",
  initialState,
  reducers: {
    updateFilterResource: (state: IResourceStore, action: PayloadAction<IFilterResource>) => {
      state.filterResource = { ...action.payload };
    },
    selectResource: (state: IResourceStore, action: PayloadAction<ResourceInfo>) => {
      const resourceServiceImpl: ResourceService = new ResourceService();

      const resourceSelected: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      let newResource = action.payload;

      newResource = copyOrderFromOriginal(state, newResource);
      // newResource.uuid = UidGenerator.NewUid();

      if (!resourceSelected.map((item) => item.email).includes(newResource.email)) {
        resourceSelected.push(newResource);
      }
      state.selectedResource = resourceSelected;
      resourceServiceImpl.toggleSelectResource(newResource);
    },
    selectResourceDialog: (state: IResourceStore, action: PayloadAction<ResourceInfo>) => {
      const resourceSelected: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      resourceSelected.push(action.payload);
      state.selectedResource = resourceSelected;
    },
    updateResourcesDataInSelectedChangeRsOutlook: (state: IResourceStore, action: PayloadAction<{ resources: ResourceInfo[]; orgInfo: OrganizationInfo }>) => {
      let rsTemp: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      rsTemp = rsTemp.concat(action.payload.resources);
      state.selectedResource = rsTemp;
      state.loading = false;
    },
    updateAllResourcesDataInSelectedChangeRsOutlook: (state: IResourceStore, action: PayloadAction<ResourceInfo[]>) => {
      state.selectedResource = cloneDeep(action.payload);
      state.loading = false;
    },
    deleteSelectResource: (state: IResourceStore, action: PayloadAction<ResourceInfo>) => {
      const resourceServiceImpl: ResourceService = new ResourceService();

      const resourceSelected: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      state.selectedResource = resourceSelected.filter((item: ResourceInfo) => item.guid !== action.payload.guid);
      resourceServiceImpl.toggleRemoveResource(action.payload);
    },
    deleteSelectResourceDialog: (state: IResourceStore, action: PayloadAction<ResourceInfo>) => {
      const resourceSelected: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      state.selectedResource = resourceSelected.filter((item: ResourceInfo) => item.guid !== action.payload.guid);
    },
    deleteSelectWhenFromOutlook: (state: IResourceStore, action: PayloadAction<string[]>) => {
      console.log("deleteSelectWhenFromOutlook", action.payload);

      const resourceSelected: ResourceInfo[] = cloneDeep(state.selectedResource || []);
      const attendees: AttendeeInfo[] = cloneDeep(state.attendees.listAttendee || []);
      state.selectedResource = resourceSelected.filter((item: ResourceInfo) => !action.payload.includes(item.email));
      state.attendees.listAttendee = attendees.filter((item: AttendeeInfo) => !action.payload.includes(item.email));
    },
    updateResourceSelectedFromOutlook: (state: IResourceStore, action: PayloadAction<OutlookRecipientDto[]>) => {
      state.resourceSelectedInOutlook = cloneDeep(action.payload);
    },
    addAttendee: (state: IResourceStore, action: PayloadAction<AttendeeInfo>) => {
      const newAttendee = action.payload;
      const attendeeExists = state.attendees.listAttendee.some((attendee) => attendee.email.toLowerCase() === newAttendee.email.toLowerCase());

      if (!attendeeExists && newAttendee.email?.toLowerCase() !== state.attendees.mainAttendee?.email?.toLowerCase()) {
        const attendeeClone = cloneDeep(state.attendees.listAttendee);
        attendeeClone.push(newAttendee);
        state.attendees.listAttendee = attendeeClone;
      }
      // If attendee already exists, we don't modify the state
    },
    deleteAttendee: (state: IResourceStore, action: PayloadAction<AttendeeInfo>) => {
      const resourceServiceImpl: ResourceService = new ResourceService();

      const attendees: AttendeeInfo[] = cloneDeep(state.attendees.listAttendee || []);
      state.attendees.listAttendee = attendees.filter((item: AttendeeInfo) => item.email !== action.payload.email);
      resourceServiceImpl.toggleRemoveResource(action.payload as ResourceInfo);
    },
    updateMainAttendee: (state: IResourceStore, action: PayloadAction<AttendeeInfo>) => {
      const newAttendee = action.payload;
      state.attendees.mainAttendee = newAttendee;
      // If attendee already exists, we don't modify the state
    },
    updateLoadingSelectedRs: (state: IResourceStore, action: PayloadAction<boolean>) => {
      state.isLoadingSelectedRs = action.payload;
    },
    setOriginalSelectedResources: (state: IResourceStore, action: PayloadAction<ResourceInfo[]>) => {
      state.originalSelectedResources = cloneDeep(action.payload);
    },
    setResourceProcessingMoving: (state: IResourceStore, action: PayloadAction<ResourceInfo[]>) => {
      state.resourceProcessingMoving = cloneDeep(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAndUpdateResources.fulfilled, (state, action) => {
        if (state.originalSelectedResources && state.originalSelectedResources.length === 0) {
          state.originalSelectedResources = cloneDeep(action.payload);
        }
      })
      .addCase(fetchAndUpdateResources.rejected, (state, action) => {
        console.error("Failed to fetch resources:", action.error);
      });
  },
});

export const {
  updateFilterResource,
  selectResource,
  deleteSelectResource,
  updateResourceSelectedFromOutlook,
  deleteSelectWhenFromOutlook,
  updateResourcesDataInSelectedChangeRsOutlook,
  updateAllResourcesDataInSelectedChangeRsOutlook,
  selectResourceDialog,
  deleteSelectResourceDialog,
  addAttendee,
  updateMainAttendee,
  deleteAttendee,
  updateLoadingSelectedRs,
  setOriginalSelectedResources,
  setResourceProcessingMoving,
} = resourceSlice.actions;

export default resourceSlice.reducer;
